import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {HsvSockets} from "#material/nodes/color/hsv/definition"
import {hsvDefinition} from "#material/nodes/color/hsv/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeConvert, threeHsvToRgb, threeRGBColor, threeRgbToHsv, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {ThreeReturns} from "#material/sockets/three"
import type {ColorNode, HsvNode} from "#material/cycles/types"
import {cyclesColor} from "#material/cycles/helpers"
import {threeAdjust, threeFacAndClamp} from "#material/nodes/color/hsv/constants"

@registerMaterialGraphNode
export class Hsv extends MaterialNode<HsvSockets> {
    public override get definition() {
        return hsvDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<HsvSockets>> => {
        if (this.parameters.ui?.disabled) {
            return {
                color:
                    (await evaluator.get(this.parameters.inputs.color))?.toColor() ??
                    threeConvert(this.parameters.settings.color, threeRGBColor) ??
                    threeRGBColor({
                        r: 0,
                        g: 0,
                        b: 0,
                    }),
            }
        }

        const {color, hue, saturation, value, fac} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            hue: this.parameters.inputs.hue,
            saturation: this.parameters.inputs.saturation,
            value: this.parameters.inputs.value,
            fac: this.parameters.inputs.fac,
        })
        const colorValue = (
            color?.toColor() ??
            threeConvert(this.parameters.settings.color, threeRGBColor) ??
            threeRGBColor({
                r: 0,
                g: 0,
                b: 0,
            })
        ).toVar()
        const hueValue = hue?.toFloat() ?? threeConvert(this.parameters.settings.hue, threeValue) ?? threeValue(1)
        const saturationValue = saturation?.toFloat() ?? threeConvert(this.parameters.settings.saturation, threeValue) ?? threeValue(1)
        const valueValue = value?.toFloat() ?? threeConvert(this.parameters.settings.value, threeValue) ?? threeValue(1)
        const facValue = fac?.toFloat() ?? threeConvert(this.parameters.settings.fac, threeValue) ?? threeValue(1)

        const hsvInput = threeRgbToHsv(colorValue)
        const hsvOutput = threeAdjust(hsvInput, hueValue, saturationValue, valueValue)

        return {color: threeFacAndClamp(colorValue, threeHsvToRgb(hsvOutput), facValue).toVar()}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<HsvSockets>> => {
        if (this.parameters.ui?.disabled) {
            const inputColor = await evaluator.get(this.parameters.inputs.color)
            if (inputColor) {
                return {color: inputColor}
            } else {
                const colorNode: ColorNode = {
                    type: "color",
                    inputs: {},
                    parameters: {value: cyclesColor(this.parameters.settings.color)},
                    resources: {},
                }
                return {color: [colorNode, "color"]}
            }
        }

        const hsvNode: HsvNode = {
            type: "hsv",
            inputs: await evaluator.getAll({
                hue: this.parameters.inputs.hue,
                color: this.parameters.inputs.color,
                saturation: this.parameters.inputs.saturation,
                value: this.parameters.inputs.value,
                fac: this.parameters.inputs.fac,
            }),
            parameters: {
                fac: this.parameters.settings.fac,
                hue: this.parameters.settings.hue,
                saturation: this.parameters.settings.saturation,
                value: this.parameters.settings.value,
                color: cyclesColor(this.parameters.settings.color),
            },
            resources: {},
        }

        return {color: [hsvNode, "color"]}
    }
}
